import React, { useEffect, useState } from 'react';

import { INCOME_TAX_HEAD_OPTIONS, getOptionLabel } from '../constants/taxOptions';
import CombinedClientScopeFilters from './CombinedClientScopeFilters';
import useCombinedReportScope from './useCombinedReportScope';
import { fetchCombinedTaxComputation, resolveCombinedReportError } from './combinedReportUtils';
import '../tax-computation/TaxComputation.css';
import '../consolidated-reports/ConsolidatedReports.css';

const apiUrl = process.env.REACT_APP_API_URL;

const formatAmount = (value) => {
  const number = Number(value ?? 0);
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const summaryRows = (data) => [
  { label: 'F&O Net Income', amount: data.fnoNet },
  { label: 'Intraday Net Income', amount: data.intradayNet },
  { label: 'Delivery Business Net Income', amount: data.deliveryBusinessNet },
  { label: 'Business Income Before Tax Depreciation', amount: data.businessIncomeBeforeTaxDepreciation },
  { label: 'Less: Tax Depreciation', amount: data.taxDepreciation },
  { label: 'Business Income After Tax Depreciation', amount: data.businessIncomeAfterTaxDepreciation },
  { label: 'Dividend Income', amount: data.dividendIncome },
  { label: 'Interest Income', amount: data.interestIncome },
  { label: 'Other Sources Income', amount: data.otherSourcesIncome },
  { label: 'Total Manual Income', amount: data.otherIncome },
  { label: 'Short-Term Capital Gain', amount: data.shortTermCapitalGain },
  { label: 'Long-Term Capital Gain Before 112A Relief', amount: data.longTermCapitalGainGross },
  { label: 'Less: 112A Threshold Relief', amount: data.longTermCapitalGainThresholdRelief },
  { label: 'Taxable Long-Term Capital Gain', amount: data.longTermCapitalGain },
  { label: 'Gross Total Income', amount: data.grossTotalIncome, highlight: true },
];

const CombinedTaxComputation = () => {
  const {
    financialYear,
    clientOptions,
    clientsLoading,
    clientScopeMode,
    selectedClientIds,
    effectiveClientIds,
    scopeSummary,
    selectionPreview,
    onScopeModeChange,
    onToggleClientId,
  } = useCombinedReportScope();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!financialYear || effectiveClientIds.length === 0) {
      setData(null);
      setError('');
      setLoading(false);
      return;
    }

    let ignore = false;
    setLoading(true);

    fetchCombinedTaxComputation(apiUrl, effectiveClientIds, financialYear, {
      allAccessibleClients: clientScopeMode === 'all',
    })
      .then((response) => {
        if (ignore) {
          return;
        }
        setData(response);
        setError('');
      })
      .catch((requestError) => {
        if (!ignore) {
          setError(resolveCombinedReportError(requestError, 'Unable to load the combined tax computation report.'));
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [clientScopeMode, effectiveClientIds, financialYear]);

  return (
    <div className="tax-computation-page combined-report-page">
      <div className="combined-report-header">
        <p className="consolidated-reports-eyebrow">Combined Reports</p>
        <h2>Combined Tax Computation</h2>
        <p className="financial-page-subtitle">See one cross-client tax working summary, including manual incomes and depreciation schedules for the selected reporting scope.</p>
      </div>

      <CombinedClientScopeFilters
        clientOptions={clientOptions}
        clientsLoading={clientsLoading}
        clientScopeMode={clientScopeMode}
        selectedClientIds={selectedClientIds}
        effectiveClientIds={effectiveClientIds}
        financialYear={financialYear}
        onScopeModeChange={onScopeModeChange}
        onToggleClientId={onToggleClientId}
      />

      {!financialYear ? <p className="financial-message">Select a financial year from the header to load the combined report.</p> : null}
      {financialYear && clientOptions.length > 0 && effectiveClientIds.length === 0 ? <p className="financial-message">Choose one or more client IDs to load the combined report.</p> : null}
      {loading && <p className="financial-message">Loading combined tax computation...</p>}
      {error && <p className="financial-message financial-message--error">{error}</p>}

      {data ? (
        <>
          <section className="consolidated-reports-stat-grid">
            <article className="consolidated-reports-stat-card">
              <span>Scope</span>
              <strong>{scopeSummary}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Business Income After Tax Depreciation</span>
              <strong>₹ {formatAmount(data.businessIncomeAfterTaxDepreciation)}</strong>
            </article>
            <article className="consolidated-reports-stat-card">
              <span>Tax Depreciation</span>
              <strong>₹ {formatAmount(data.taxDepreciation)}</strong>
            </article>
            <article className="consolidated-reports-stat-card consolidated-reports-stat-card--accent">
              <span>Gross Total Income</span>
              <strong>₹ {formatAmount(data.grossTotalIncome)}</strong>
            </article>
          </section>

          <p className="combined-report-caption">Included client IDs: {selectionPreview}</p>

          <section className="tax-section">
            <h3>Tax Computation Summary</h3>
            <div className="tax-table-wrap">
              <table className="tax-table">
                <thead>
                  <tr>
                    <th>Particulars</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows(data).map((row) => (
                    <tr key={row.label} className={row.highlight ? 'tax-highlight-row' : ''}>
                      <td>{row.label}</td>
                      <td>₹ {formatAmount(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="tax-section">
            <h3>Manual Income Detail</h3>
            {data.manualIncomeEntries && data.manualIncomeEntries.length > 0 ? (
              <div className="tax-table-wrap">
                <table className="tax-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Tax Head</th>
                      <th>Income Head</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.manualIncomeEntries.map((item) => (
                      <tr key={item.combinedRowId}>
                        <td>{item.clientId}</td>
                        <td>{getOptionLabel(INCOME_TAX_HEAD_OPTIONS, item.taxHead, item.taxHead)}</td>
                        <td>{item.label}</td>
                        <td>₹ {formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="tax-computation-message">No other income entries available for this financial year.</p>
            )}
          </section>

          <section className="tax-section">
            <h3>Depreciation Schedule For Tax Purpose</h3>
            {data.depreciationSchedule?.blocks && data.depreciationSchedule.blocks.length > 0 ? (
              <div className="tax-table-wrap">
                <table className="tax-table tax-schedule-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Tax Block</th>
                      <th>Rate %</th>
                      <th>Opening WDV</th>
                      <th>Full-Rate Additions</th>
                      <th>Half-Rate Additions</th>
                      <th>Depreciation</th>
                      <th>Closing WDV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.depreciationSchedule.blocks.map((block) => (
                      <tr key={block.combinedRowId}>
                        <td>{block.clientId}</td>
                        <td>{block.taxBlock}</td>
                        <td>{formatAmount(block.depreciationRate)}</td>
                        <td>₹ {formatAmount(block.openingWdv)}</td>
                        <td>₹ {formatAmount(block.additionsEligibleForFullRate)}</td>
                        <td>₹ {formatAmount(block.additionsEligibleForHalfRate)}</td>
                        <td>₹ {formatAmount(block.depreciationForYear)}</td>
                        <td>₹ {formatAmount(block.closingWdv)}</td>
                      </tr>
                    ))}
                    <tr className="tax-highlight-row">
                      <td colSpan="6">Total</td>
                      <td>₹ {formatAmount(data.depreciationSchedule.totalTaxDepreciation)}</td>
                      <td>₹ {formatAmount(data.depreciationSchedule.totalClosingWdv)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="tax-computation-message">No tax depreciation blocks available for this financial year.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default CombinedTaxComputation;